/**
 * SupplierAktionenTab — Lieferanten Deklarationen AP (v4).
 *
 * Two modes, driven by store.activeBucketRecordId:
 *   · idle  → BucketList (selection workspace + upload + settings).
 *   · import → BucketImportView (per-file extraction + accumulated diff).
 *
 * Selection model (Importer-type architecture, §3.2):
 *   The set of buckets is a projection of selectedRecordIds joined with
 *   pending documents. On first AP entry (when selection is empty) we
 *   default-seed selectedRecordIds with all supplier records.
 *
 * Import flow (per-record sequential, §3.2.4):
 *   For each pending doc in the bucket:
 *     ingest/upload → ingest/detect → ingest/extract-explicit
 *   then accumulate every scope's writes into one merged executor result.
 *   On Anwenden the existing two-pass commitScalars + commitRelations
 *   helpers run, the bucket dissolves, and the supplier is removed from
 *   the active selection.
 */

import { useTranslation, type TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { api } from "../../../api/client"
import {
  executeIngestionTemplate,
  type TemplateExecutorResult,
} from "../../../lib/templates/executor"
import { listIngestionTemplatesForAnchor } from "../../../lib/templates/registry"
import { useStore } from "../../../store/useStore"
import type {
  DiffState,
  ExtractionEntry,
  RelationDiffEntry,
  ScopeDiffState,
} from "../../../types"
import { BucketImportView, type FileExtractionState } from "./BucketImportView"
import { BucketList } from "./BucketList"
import type { ExtractedField } from "./SupplierExtractor"
import { WebhookEmailSettings } from "./WebhookEmailSettings"

const AP_ID = "supplier"

function getApLastOpenedAt(): number | null {
  try {
    return (
      Number(localStorage.getItem("qms.ap.lastOpenedAt.supplier") ?? "0") ||
      null
    )
  } catch {
    return null
  }
}
function setApLastOpenedAt(ts: number) {
  try {
    localStorage.setItem("qms.ap.lastOpenedAt.supplier", String(ts))
  } catch {
    /* empty */
  }
}
// SQLite stores datetimes as naive UTC strings (no TZ marker). Parse as UTC
// so comparisons with Date.now() are correct in non-UTC locales.
function parseUploadedAt(s: string): number {
  const iso = s.includes("T") ? s : s.replace(" ", "T")
  return new Date(
    iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z",
  ).getTime()
}

// ---------------------------------------------------------------------------
// Commit helpers (re-used from v3 — unchanged behaviour)
// ---------------------------------------------------------------------------

async function commitScalars(
  ds: DiffState | ScopeDiffState,
  fileName: string,
  idMapAccum: Map<string, string>,
  qc: ReturnType<typeof useQueryClient>,
): Promise<number> {
  const anchoredMap = new Map(ds.anchoredRecords.map((a) => [a.record_id, a]))
  const scalarItems = ds.rawExtractions.filter(
    (ex) => ex.primitive_type !== "relation",
  )
  if (scalarItems.length === 0) return 0
  const result = await api.ingestCommit(ds.scopeId, {
    document_name: fileName,
    history_mode: "changed_only",
    extractions: scalarItems.map((e) => {
      const anchor = anchoredMap.get(e.record_id)
      return {
        record_id: e.record_id,
        primitive_id: e.primitive_id,
        value: e.value,
        source: fileName,
        confidence: e.confidence,
        extraction_type: e.extraction_type,
        needs_cleaning: e.needs_cleaning,
        confirmed_by_user: true,
        is_new: anchor?.is_new ?? false,
        pk_value: anchor?.is_new ? anchor.pk_value : undefined,
      }
    }),
  })
  for (const [synthId, realId] of Object.entries(result.id_map ?? {})) {
    idMapAccum.set(synthId, realId)
  }
  qc.invalidateQueries({ queryKey: ["records", ds.scopeId] })
  return result.committed
}

async function commitRelations(
  ds: DiffState | ScopeDiffState,
  fileName: string,
  idMapAccum: Map<string, string>,
  qc: ReturnType<typeof useQueryClient>,
): Promise<number> {
  if ((ds as ScopeDiffState).secondaryDiff === true) return 0
  if (!ds.relationDiffs) return 0
  const items: Array<{
    record_id: string
    primitive_id: string
    value: string
    mapped_id?: string | null
  }> = []
  for (const [recordId, primMap] of Object.entries(ds.relationDiffs)) {
    for (const [primitiveId, rd] of Object.entries(primMap)) {
      for (const entry of rd.entries as RelationDiffEntry[]) {
        if (entry.action !== "add") continue
        const realRecordId = idMapAccum.get(recordId) ?? recordId
        const realTargetId = idMapAccum.get(entry.targetId) ?? entry.targetId
        if (realTargetId.startsWith("new:")) continue
        items.push({
          record_id: realRecordId,
          primitive_id: primitiveId,
          value: realTargetId,
          mapped_id: entry.mappedId,
        })
      }
    }
  }
  if (items.length === 0) return 0
  const result = await api.ingestCommit(ds.scopeId, {
    document_name: fileName,
    history_mode: "changed_only",
    extractions: items.map((e) => ({
      record_id: e.record_id,
      primitive_id: e.primitive_id,
      value: e.value,
      source: fileName,
      confidence: 1,
      extraction_type: "explicit" as const,
      needs_cleaning: false,
      confirmed_by_user: true,
      is_new: false,
      mapped_id: e.mapped_id ?? undefined,
    })),
  })
  qc.invalidateQueries({ queryKey: ["records", ds.scopeId] })
  return result.committed
}

// ---------------------------------------------------------------------------
// Field-list builder (carry-over from v3, generalised across multiple files)
// ---------------------------------------------------------------------------

function buildFieldsAcrossResults(
  results: TemplateExecutorResult[],
  translate: (key: TranslationKeys) => string,
): ExtractedField[] {
  const seen = new Set<string>()
  const fields: ExtractedField[] = []
  for (const result of results) {
    const scopePk: Record<string, string> = {}
    for (const [scopeId, sd] of Object.entries(result.additional)) {
      const anchor = sd.anchoredRecords[0]
      if (anchor?.pk_value) scopePk[scopeId] = anchor.pk_value
    }
    const allScoped: Array<{ scopeId: string; entries: ExtractionEntry[] }> = [
      {
        scopeId: result.primary.scopeId,
        entries: result.primary.rawExtractions,
      },
      ...Object.values(result.additional).map((sd) => ({
        scopeId: sd.scopeId,
        entries: sd.rawExtractions,
      })),
    ]
    for (const { scopeId, entries } of allScoped) {
      const pkSuffix =
        scopeId !== result.primary.scopeId && scopePk[scopeId]
          ? ` (${scopePk[scopeId]})`
          : ""
      for (const e of entries) {
        const key = `${e.primitive_scope_id}.${e.primitive_id}`
        if (seen.has(key)) continue
        seen.add(key)
        const isRelation = e.primitive_type === "relation"
        fields.push({
          primitiveId: e.primitive_id,
          primitiveTitle: isRelation
            ? `${e.primitive_title} (${translate("QMS/AP_TYPE/IMPORTER")})${pkSuffix}`
            : `${e.primitive_title}${pkSuffix}`,
          value: isRelation ? (e.raw_value ?? "—") : e.value,
        })
      }
    }
  }
  return fields
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SupplierAktionenTab() {
  const { translate } = useTranslation()
  const queryClient = useQueryClient()
  const {
    selectedRecordIds,
    selectRecords,
    deselectRecords,
    activeBucketRecordId,
    setActiveBucket,
    clearActiveBucket,
    enterDiffMode,
    exitDiffMode,
    setActionScopeDiff,
    setNewDocumentIds,
    clearNewDocumentIds,
    newDocumentIds,
    pendingAutoImportRecordId,
    setPendingAutoImportRecordId,
  } = useStore()

  const [importing, setImporting] = useState(false)
  const [committing, setCommitting] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  const [fileStates, setFileStates] = useState<FileExtractionState[]>([])
  const [executorResults, setExecutorResults] = useState<
    Array<{ filename: string; result: TemplateExecutorResult }>
  >([])
  const [showSettings, setShowSettings] = useState(false)
  const seededRef = useRef(false)
  const seededNewDocsRef = useRef(false)
  // Record ids we've already auto-selected this mount, so the auto-select
  // effect doesn't re-add an id after Anwenden deselects it (and before
  // pendingDocs refetches the now-consumed doc out of the list).
  const autoSelectedRef = useRef<Set<string>>(new Set())
  // Snapshot captured at render time so it survives React Strict Mode's effect cycle.
  const lastOpenedAtSnapshot = useRef<number | null>(getApLastOpenedAt())

  // ── Data: suppliers (records + scope primitives) ─────────────────────────
  const { data: suppliersPage } = useQuery({
    queryKey: ["records", "supplier"],
    queryFn: () => api.listRecords("supplier"),
    staleTime: 30_000,
  })

  const allSuppliers = suppliersPage?.records ?? []
  const supplierPrims = suppliersPage?.scope?.primitives ?? []
  const pkPrim = supplierPrims.find((p) => p.is_pk) ?? null
  const namePrim =
    supplierPrims.find((p) => !p.is_pk && p.type === "string") ?? null

  // ── v6: default to NO suppliers selected. ───────────────────────────────
  // The Substanz Radar AP drives the selection by adding affected suppliers
  // when the user applies a monitor recommendation.  Manual selection still
  // works through the Datenbank tab.
  useEffect(() => {
    if (seededRef.current) return
    if (allSuppliers.length === 0) return
    seededRef.current = true
  }, [allSuppliers])

  // ── Data: pending documents ──────────────────────────────────────────────
  const { data: pendingDocs = [], isSuccess: pendingDocsSuccess } = useQuery({
    queryKey: ["pendingDocuments", AP_ID],
    queryFn: () => api.pendingDocumentsList(AP_ID),
    refetchInterval: 8000,
  })

  // ── Record "opened at" immediately; reset seeding guard + clear highlights on unmount.
  //    MUST be declared before the seeding effect so its cleanup runs first in React
  //    Strict Mode (cleanup order = registration order), allowing the seeding effect
  //    to re-run after the Strict Mode fake-unmount/remount cycle.
  useEffect(() => {
    setApLastOpenedAt(Date.now())
    return () => {
      seededNewDocsRef.current = false
      clearNewDocumentIds()
    }
    // clearNewDocumentIds is a stable Zustand action — effectively mount/unmount only.
  }, [clearNewDocumentIds])

  // ── Seed new-document IDs on first successful query load ─────────────────
  useEffect(() => {
    if (seededNewDocsRef.current || !pendingDocsSuccess) return
    const lastOpenedAt = lastOpenedAtSnapshot.current
    const newIds = new Set(
      pendingDocs
        .filter(
          (d) =>
            d.source !== "upload" &&
            (!lastOpenedAt || parseUploadedAt(d.uploaded_at) > lastOpenedAt),
        )
        .map((d) => d.id),
    )
    setNewDocumentIds(newIds)
    seededNewDocsRef.current = true
  }, [pendingDocsSuccess, pendingDocs, setNewDocumentIds])

  // ── Auto-select suppliers that have monitor-staged pending docs ──────────
  // BucketList only renders selected suppliers, so a doc staged by the
  // Substanz-Radar action (source='monitor') wouldn't appear until the user
  // manually added the supplier from the Datenbank tab. We add an id at
  // most once per AP mount; after Anwenden consumes the doc and deselects
  // the supplier, we won't re-add it from the cached pendingDocs list.
  useEffect(() => {
    if (!pendingDocsSuccess) return
    const toAdd: string[] = []
    for (const d of pendingDocs) {
      if (d.source === 'upload') continue
      const id = d.target_record_id
      if (!id) continue
      if (autoSelectedRef.current.has(id)) continue
      if (selectedRecordIds.has(id)) {
        autoSelectedRef.current.add(id)
        continue
      }
      toAdd.push(id)
    }
    if (toAdd.length > 0) {
      for (const id of toAdd) autoSelectedRef.current.add(id)
      selectRecords(toAdd)
    }
  }, [pendingDocsSuccess, pendingDocs, selectedRecordIds, selectRecords])

  // ── Selection-bucket equivalence (§3.1) ──────────────────────────────────
  // Buckets correspond to selected supplier records that exist in the DB.
  const selectedSupplierEntries = useMemo(() => {
    const map = new Map(allSuppliers.map((r) => [r.id, r]))
    const out: Array<{ recordId: string; name: string; pk: string | null }> = []
    for (const id of selectedRecordIds) {
      const r = map.get(id)
      if (!r) continue
      const pk = pkPrim ? (r.values[pkPrim.name]?.value ?? null) : null
      const name = namePrim
        ? (r.values[namePrim.name]?.value ?? pk ?? r.id)
        : (pk ?? r.id)
      out.push({ recordId: r.id, name, pk })
    }
    out.sort((a, b) => a.name.localeCompare(b.name, "de"))
    return out
  }, [allSuppliers, selectedRecordIds, pkPrim, namePrim])

  // ── Mutations ────────────────────────────────────────────────────────────
  const refreshDocs = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["pendingDocuments", AP_ID] })
  }, [queryClient])

  const handleUpload = useCallback(
    async (files: File[], targetRecordId: string | null | undefined) => {
      try {
        for (const file of files) {
          const created = await api.pendingDocumentUpload(AP_ID, file, "upload")
          // If user dropped on a specific bucket, override the auto-assignment.
          if (
            targetRecordId !== undefined &&
            created.target_record_id !== targetRecordId
          ) {
            await api.pendingDocumentReassign(AP_ID, created.id, targetRecordId)
          }
        }
      } catch (err) {
        console.error("[SupplierAktionenTab] upload failed", err)
      } finally {
        refreshDocs()
      }
    },
    [refreshDocs],
  )

  const handleReassign = useCallback(
    async (docId: string, target: string | null) => {
      try {
        await api.pendingDocumentReassign(AP_ID, docId, target)
      } finally {
        refreshDocs()
      }
    },
    [refreshDocs],
  )

  const handleDelete = useCallback(
    async (docId: string) => {
      try {
        await api.pendingDocumentDelete(AP_ID, docId)
      } finally {
        refreshDocs()
      }
    },
    [refreshDocs],
  )

  const handleRemoveFromSelection = useCallback(
    (recordId: string) => {
      deselectRecords([recordId])
    },
    [deselectRecords],
  )

  const downloadUrl = useCallback(
    (docId: string) => api.pendingDocumentDownloadUrl(AP_ID, docId, true),
    [],
  )

  // ── Bucket import flow ───────────────────────────────────────────────────
  const handleImport = useCallback(
    async (recordId: string) => {
      const docs = pendingDocs.filter((d) => d.target_record_id === recordId)
      if (docs.length === 0) return

      setImporting(true)
      setImportError(null)
      setExecutorResults([])
      setFileStates(docs.map((d) => ({ doc: d, status: "pending" })))
      setActiveBucket(recordId)

      const templates = listIngestionTemplatesForAnchor("supplier")
      if (templates.length === 0) {
        setImportError(translate("QMS/SUPPLIER/NO_TEMPLATE"))
        setImporting(false)
        return
      }
      const template = templates[0]

      // Sequentially: per doc, run executor. The executor reads template-driven
      // mock data; the original blob is preserved on the backend (download URL).
      const results: Array<{
        filename: string
        result: TemplateExecutorResult
      }> = []
      for (let i = 0; i < docs.length; i++) {
        const doc = docs[i]
        try {
          setFileStates((prev) =>
            prev.map((f, idx) =>
              idx === i ? { ...f, status: "extracting" } : f,
            ),
          )
          const result = await executeIngestionTemplate(
            template,
            { scopeId: "supplier", recordId, fileName: doc.filename },
            { qc: queryClient },
          )
          results.push({ filename: doc.filename, result })
          setFileStates((prev) =>
            prev.map((f, idx) => (idx === i ? { ...f, status: "done" } : f)),
          )
        } catch (err) {
          const msg =
            err instanceof Error ? err.message : translate("QMS/SUPPLIER/EXTRACTION_FAILED")
          console.error(
            "[SupplierAktionenTab] extraction failed for",
            doc.filename,
            err,
          )
          setFileStates((prev) =>
            prev.map((f, idx) =>
              idx === i ? { ...f, status: "failed", error: msg } : f,
            ),
          )
        }
      }

      setExecutorResults(results)
      setImporting(false)

      // Push the first successful result's diffs into actionState so the
      // Datenbank tab transitions to diff overlay for this anchored record.
      const first = results[0]?.result
      if (first) {
        enterDiffMode(first.primary)
        for (const sd of Object.values(first.additional)) {
          setActionScopeDiff(sd.scopeId, sd)
        }
      }
    },
    [
      pendingDocs,
      queryClient,
      setActiveBucket,
      enterDiffMode,
      setActionScopeDiff,
    ],
  )

  // Simulation engine hook: when chat says "Ja → Dokumente importieren", it
  // sets pendingAutoImportRecordId. Once the bucket has at least one pending
  // doc, auto-fire handleImport.
  useEffect(() => {
    if (!pendingAutoImportRecordId) return
    if (importing || activeBucketRecordId) return
    const hasDocs = pendingDocs.some(
      (d) => d.target_record_id === pendingAutoImportRecordId,
    )
    if (!hasDocs) return
    const target = pendingAutoImportRecordId
    setPendingAutoImportRecordId(null)
    void handleImport(target)
  }, [
    pendingAutoImportRecordId,
    pendingDocs,
    importing,
    activeBucketRecordId,
    setPendingAutoImportRecordId,
    handleImport,
  ])

  const fields = useMemo(
    () => buildFieldsAcrossResults(executorResults.map((x) => x.result), translate),
    [executorResults, translate],
  )

  const handleVerwerfen = useCallback(() => {
    setExecutorResults([])
    setFileStates([])
    setImportError(null)
    setImporting(false)
    clearActiveBucket()
    exitDiffMode()
  }, [clearActiveBucket, exitDiffMode])

  const handleUebernehmen = useCallback(async () => {
    if (executorResults.length === 0 || !activeBucketRecordId) return
    setCommitting(true)
    setImportError(null)
    try {
      let totalCommitted = 0
      // Apply each file's executor result independently. The diff slot has
      // already been merged for display; for commit, run the v3 two-pass per
      // result so id_maps thread correctly and relations resolve their synth ids.
      for (const { filename, result } of executorResults) {
        const allScopes: (DiffState | ScopeDiffState)[] = [
          result.primary,
          ...Object.values(result.additional),
        ]
        const idMap = new Map<string, string>()
        for (const sd of allScopes)
          totalCommitted += await commitScalars(
            sd,
            filename,
            idMap,
            queryClient,
          )
        for (const sd of allScopes)
          totalCommitted += await commitRelations(
            sd,
            filename,
            idMap,
            queryClient,
          )
      }

      // Consume the docs (deletes pending_documents rows).
      const recordId = activeBucketRecordId
      const consumedDocs = pendingDocs.filter(
        (d) => d.target_record_id === recordId,
      )
      await Promise.all(
        consumedDocs.map((d) =>
          api.pendingDocumentConsume(AP_ID, d.id).catch(() => {}),
        ),
      )

      // If the bucket carried a monitor-staged doc, cascade-refresh the
      // supplier + every component it delivers. The synthetic web-search
      // .txt has no parseable data so the regular commit pipeline is a
      // no-op for those records, and without this the freshness cascade
      // would stay stuck after the radar action.
      const hadMonitorDoc = consumedDocs.some(d => d.source === 'monitor')
      if (hadMonitorDoc) {
        await api.monitorSupplierCascade({
          supplier_record_id: recordId,
          direction: 'refresh',
        }).catch(() => {})
      }

      // Build a name for the activity log.
      const supplier = allSuppliers.find((s) => s.id === recordId)
      const supplierName =
        supplier && namePrim
          ? (supplier.values[namePrim.name]?.value ?? supplier.id)
          : recordId

      await api
        .apActivityAppend({
          ap_id: AP_ID,
          kind: "supplier_commit",
          summary: `${consumedDocs.length} ${consumedDocs.length === 1 ? translate("QMS/SUPPLIER/DOCUMENT_SINGULAR") : translate("QMS/SUPPLIER/DOCUMENT_PLURAL")} · ${totalCommitted} ${translate("QMS/DETAIL/FIELD_PLURAL")} · ${supplierName}`,
          metadata: {
            record_id: recordId,
            documents: consumedDocs.map((d) => d.filename),
            committed: totalCommitted,
          },
        })
        .catch(() => {})

      queryClient.invalidateQueries({ queryKey: ["ap-activity"] })
      // v6: refresh customer freshness so the customer-conformity AP card
      // picks up newly-fresh customers as soon as the user returns to the
      // landing page.
      queryClient.invalidateQueries({ queryKey: ["freshness"] })
      refreshDocs()

      // Dissolve bucket: deselect supplier, exit import mode.
      deselectRecords([recordId])
      handleVerwerfen()
    } catch (err) {
      setImportError(
        err instanceof Error ? err.message : translate("QMS/SUPPLIER/COMMIT_FAILED"),
      )
    } finally {
      setCommitting(false)
    }
  }, [
    executorResults,
    activeBucketRecordId,
    pendingDocs,
    allSuppliers,
    namePrim,
    queryClient,
    deselectRecords,
    handleVerwerfen,
    refreshDocs,
  ])

  // ── Render ───────────────────────────────────────────────────────────────
  if (activeBucketRecordId) {
    const supplier = allSuppliers.find((s) => s.id === activeBucketRecordId)
    const supplierName =
      supplier && namePrim
        ? (supplier.values[namePrim.name]?.value ?? activeBucketRecordId)
        : activeBucketRecordId
    const supplierPk =
      supplier && pkPrim ? (supplier.values[pkPrim.name]?.value ?? null) : null

    return (
      <BucketImportView
        supplierName={supplierName ?? activeBucketRecordId}
        supplierPk={supplierPk}
        files={fileStates}
        fields={fields}
        onFieldEdit={() => {
          /* edits are display-only in v4, like v3 */
        }}
        onVerwerfen={handleVerwerfen}
        onUebernehmen={handleUebernehmen}
        onBack={handleVerwerfen}
        isCommitting={committing}
        error={importError}
      />
    )
  }

  return (
    <div className="flex h-full flex-col">
      <BucketList
        suppliers={selectedSupplierEntries}
        allSuppliers={allSuppliers}
        pkPrimitiveName={pkPrim?.name ?? null}
        namePrimitiveName={namePrim?.name ?? null}
        pendingDocs={pendingDocs}
        downloadUrl={downloadUrl}
        onUpload={handleUpload}
        onReassign={handleReassign}
        onDelete={handleDelete}
        onImport={handleImport}
        onRemoveFromSelection={handleRemoveFromSelection}
        isImporting={importing}
        importingRecordId={activeBucketRecordId}
        newDocumentIds={newDocumentIds}
      />
      <div className="border-t border-grey4 px-6 py-3">
        <button
          onClick={() => setShowSettings((v) => !v)}
          className="text-xs text-grey5 hover:text-white"
        >
          ⚙ {translate("QMS/SUPPLIER/SETTINGS_TOGGLE")}{" "}
          {showSettings ? "▴" : "▾"}
        </button>
        {showSettings && (
          <div className="mt-3">
            <WebhookEmailSettings apId={AP_ID} />
          </div>
        )}
      </div>
    </div>
  )
}
